// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import type { Bundle } from '../data';
import { stateColour, stateName } from '../data';
import { num, signed, pct1, safe, tipAttr, escapeHtml } from '../format';

/**
 * The pivot of the whole argument, and a deflation.
 *
 * If Canberra's share of the APS fell, the intuitive explanation is that its
 * staff moved to the states. This view shows they did not: the interstate flows
 * are small next to the growth, and they run the wrong way to explain it. The
 * jobs were CREATED outside Canberra rather than relocated from it.
 *
 * Drawn as a chord over the 9x9 origin-destination matrix. Only the NET residue
 * of each pair is coloured — the matched reciprocal flow is drawn as a neutral
 * band — so there are at most 36 marks instead of 72 and the asymmetries are
 * what the eye lands on.
 */

interface Arc { key: string; start: number; end: number; out: number; in: number }

export function renderMoves(root: HTMLElement, b: Bundle): void {
  let mode: 'net' | 'gross' = 'net';
  let selected: string | null = null;

  const cols = b.movement.columns;
  const rows = b.movement.rows;
  const flow = (from: string, to: string): number | null => {
    const r = rows.find((x) => x.label === from);
    if (!r) return null;
    const c = r.cells.find((x) => x.key === to);
    return c ? c.value : null;
  };
  const suppressed = (from: string, to: string): boolean => {
    const r = rows.find((x) => x.label === from);
    return !!r?.cells.find((x) => x.key === to)?.suppressed;
  };

  const totalMoves = rows.reduce((s, r) => s + r.cells.reduce((t, c) => t + (c.value ?? 0), 0), 0);

  root.innerHTML = `
    <div class="view-head">
      <h2>Did anybody actually move?</h2>
      <p>Every interstate move by an ongoing APS employee during the year. Each arc is a state; the sliver between two arcs is the <em>net</em> flow — the residue left after matching moves in both directions against each other.</p>
    </div>
    <div class="controls">
      <div class="control-group">
        <span class="control-label">Show</span>
        <div class="seg" role="group" aria-label="Flow mode">
          <button type="button" data-mode="net" aria-pressed="true">Net flow</button>
          <button type="button" data-mode="gross" aria-pressed="false">All moves</button>
        </div>
      </div>
      <button type="button" class="icon-btn" id="moves-clear" style="background:var(--bg-elevated);color:var(--text-secondary);border-color:var(--border-default)">Clear selection</button>
    </div>
    <div class="callout" id="moves-callout"></div>
    <div class="panel">
      <div class="chart-wrap" id="chord-wrap" style="max-width:660px;margin-inline:auto"></div>
      <div class="legend" id="chord-legend"></div>
    </div>
    <div class="panel">
      <div class="section-title">Net ledger</div>
      <div class="section-sub">Moves out, moves in, and the difference, for each state. A confidentialised cell is shown as withheld — it is not zero.</div>
      <div class="scroller"><table class="data" id="moves-table"></table></div>
    </div>`;

  function arcs(): Arc[] {
    const totals = cols.map((c) => {
      const out = rows.find((r) => r.label === c)?.cells.reduce((s, x) => s + (x.value ?? 0), 0) ?? 0;
      const inn = rows.reduce((s, r) => s + (r.cells.find((x) => x.key === c)?.value ?? 0), 0);
      return { key: c, out, in: inn, size: out + inn };
    });
    const sum = totals.reduce((s, t) => s + t.size, 0) || 1;
    const GAP = 0.022;
    let a = -Math.PI / 2;
    return totals.map((t) => {
      const span = (t.size / sum) * (Math.PI * 2 - GAP * totals.length);
      const arc = { key: t.key, start: a, end: a + span, out: t.out, in: t.in };
      a += span + GAP;
      return arc;
    });
  }

  function draw(): void {
    const A = arcs();
    const S = 640, C = S / 2, RO = 250, RI = 228;
    const pos = new Map(A.map((x) => [x.key, x]));
    const pt = (ang: number, r: number) => [safe(C + Math.cos(ang) * r), safe(C + Math.sin(ang) * r)];

    // Ribbon endpoints are allocated proportionally along each arc.
    const cursor = new Map<string, number>(A.map((x) => [x.key, x.start]));
    const ribbons: string[] = [];
    const pairs: { a: string; b: string; ab: number; ba: number; net: number }[] = [];
    for (let i = 0; i < cols.length; i++) {
      for (let j = i + 1; j < cols.length; j++) {
        const a = cols[i], bK = cols[j];
        const ab = flow(a, bK) ?? 0;
        const ba = flow(bK, a) ?? 0;
        if (!ab && !ba) continue;
        pairs.push({ a, b: bK, ab, ba, net: ab - ba });
      }
    }
    const totalPairFlow = pairs.reduce((s, p) => s + p.ab + p.ba, 0) || 1;

    for (const p of pairs) {
      const arcA = pos.get(p.a)!, arcB = pos.get(p.b)!;
      const spanA = (arcA.end - arcA.start);
      const spanB = (arcB.end - arcB.start);
      const shareA = (p.ab + p.ba) / ((arcA.out + arcA.in) || 1);
      const shareB = (p.ab + p.ba) / ((arcB.out + arcB.in) || 1);
      const a0 = cursor.get(p.a)!, a1 = a0 + spanA * shareA;
      const b0 = cursor.get(p.b)!, b1 = b0 + spanB * shareB;
      cursor.set(p.a, a1); cursor.set(p.b, b1);

      const dim = selected && selected !== p.a && selected !== p.b;
      const winner = p.net === 0 ? null : (p.net > 0 ? p.b : p.a);
      const colour = mode === 'gross'
        ? '#9bacbd'
        : (winner ? stateColour(winner) : '#9bacbd');
      const weight = mode === 'gross' ? (p.ab + p.ba) : Math.abs(p.net);
      if (!weight) continue;
      const opacity = mode === 'gross' ? 0.3 : 0.62;

      const [x0, y0] = pt(a0, RI), [x1, y1] = pt(a1, RI);
      const [x2, y2] = pt(b0, RI), [x3, y3] = pt(b1, RI);
      const d = `M${x0.toFixed(1)},${y0.toFixed(1)} A${RI},${RI} 0 0 1 ${x1.toFixed(1)},${y1.toFixed(1)} `
        + `Q${C},${C} ${x2.toFixed(1)},${y2.toFixed(1)} A${RI},${RI} 0 0 1 ${x3.toFixed(1)},${y3.toFixed(1)} Q${C},${C} ${x0.toFixed(1)},${y0.toFixed(1)} Z`;
      ribbons.push(
        `<path class="bar${dim ? ' dimmed' : ''}" d="${d}" fill="${colour}" fill-opacity="${opacity}" `
        + `stroke="${colour}" stroke-width="0.4" tabindex="0" role="button" data-pair="${escapeHtml(p.a + '|' + p.b)}" `
        + `data-tip="${escapeHtml(tipAttr([
          `${stateName(p.a)} ⇄ ${stateName(p.b)}`,
          `${stateName(p.a)} → ${stateName(p.b)}: ${suppressed(p.a, p.b) ? 'withheld' : num(p.ab)}`,
          `${stateName(p.b)} → ${stateName(p.a)}: ${suppressed(p.b, p.a) ? 'withheld' : num(p.ba)}`,
          `Net: ${signed(p.net)} toward ${winner ? stateName(winner) : 'neither'}`,
          `${pct1(((p.ab + p.ba) / totalPairFlow) * 100)} of all interstate moves`,
        ]))}"><title></title></path>`,
      );
    }

    const arcPaths = A.map((x) => {
      const [x0, y0] = pt(x.start, RO), [x1, y1] = pt(x.end, RO);
      const [x2, y2] = pt(x.end, RI), [x3, y3] = pt(x.start, RI);
      const large = x.end - x.start > Math.PI ? 1 : 0;
      const mid = (x.start + x.end) / 2;
      const [lx, ly] = pt(mid, RO + 26);
      const dim = selected && selected !== x.key;
      return `<g>
        <path class="bar${dim ? ' dimmed' : ''}" d="M${x0.toFixed(1)},${y0.toFixed(1)} A${RO},${RO} 0 ${large} 1 ${x1.toFixed(1)},${y1.toFixed(1)} L${x2.toFixed(1)},${y2.toFixed(1)} A${RI},${RI} 0 ${large} 0 ${x3.toFixed(1)},${y3.toFixed(1)} Z"
          fill="${stateColour(x.key)}" tabindex="0" role="button" data-arc="${escapeHtml(x.key)}"
          data-tip="${escapeHtml(tipAttr([
            stateName(x.key),
            `Moved out: ${num(x.out)}`,
            `Moved in: ${num(x.in)}`,
            `Net: ${signed(x.in - x.out)}`,
          ]))}"><title></title></path>
        <text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="13" text-anchor="middle"
              fill="${dim ? 'var(--text-muted)' : 'var(--text-primary)'}" font-weight="600">${escapeHtml(x.key)}</text>
      </g>`;
    }).join('');

    root.querySelector('#chord-wrap')!.innerHTML = `
      <svg class="chart" viewBox="0 0 ${S} ${S}" role="img"
           aria-label="Chord diagram of interstate moves by ongoing APS employees">
        ${ribbons.join('')}${arcPaths}
      </svg>`;

    const act = A.find((x) => x.key === 'ACT');
    const actNet = act ? act.in - act.out : 0;
    const actGrowth = (b.map.regions.find((r) => r.name === 'Australian Capital Territory')?.values[String(b.map.latestYear)] ?? 0)
      - (b.map.regions.find((r) => r.name === 'Australian Capital Territory')?.values[String(b.map.baseYear)] ?? 0);
    root.querySelector('#moves-callout')!.innerHTML =
      `<strong>${num(totalMoves)} ongoing employees changed state</strong> in the year to ${b.meta.asAt.slice(0, 4)}. `
      + `The Australian Capital Territory was net ${signed(actNet)} on those moves — `
      + `against headcount growth of ${signed(actGrowth)} since ${b.map.baseYear}. `
      + `Relocation does not explain the shift: the jobs were created outside Canberra rather than moved out of it.`;

    root.querySelector('#chord-legend')!.innerHTML = cols.map((c) =>
      `<span class="legend-item clickable" data-arc="${escapeHtml(c)}">`
      + `<span class="legend-swatch" style="background:${stateColour(c)}"></span>${escapeHtml(stateName(c))}</span>`).join('');

    root.querySelector('#moves-table')!.innerHTML = `
      <thead><tr><th>State</th><th class="n">Moved out</th><th class="n">Moved in</th><th class="n">Net</th></tr></thead>
      <tbody>${A.map((x) => `
        <tr class="clickable" data-arc="${escapeHtml(x.key)}">
          <td>${escapeHtml(stateName(x.key))}</td>
          <td class="n">${num(x.out)}</td><td class="n">${num(x.in)}</td>
          <td class="n" style="color:${x.in - x.out >= 0 ? 'var(--grow)' : 'var(--shrink)'}">${signed(x.in - x.out)}</td>
        </tr>`).join('')}</tbody>`;
  }

  root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((btn) => {
    btn.addEventListener('click', () => {
      mode = btn.dataset.mode as 'net' | 'gross';
      root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((x) =>
        x.setAttribute('aria-pressed', String(x === btn)));
      draw();
    });
  });
  root.querySelector('#moves-clear')!.addEventListener('click', () => { selected = null; draw(); });
  root.addEventListener('click', (e) => {
    const el = (e.target as Element).closest('[data-arc]');
    if (el) {
      const k = el.getAttribute('data-arc')!;
      selected = selected === k ? null : k;
      draw();
      return;
    }
    const pair = (e.target as Element).closest('[data-pair]');
    if (pair) {
      const [a] = pair.getAttribute('data-pair')!.split('|');
      selected = selected === a ? null : a;
      draw();
    }
  });

  draw();
}
